import React from 'react';
import { EventProps } from 'react-big-calendar';
import { Tooltip } from 'primereact/tooltip';
import { parseISO, format } from 'date-fns';
import { es } from 'date-fns/locale';
import { CANCELED_BY_OPTIONS, Reservation, RESERVATION_STATUSES } from '../../types/reservation';
import { ReservationType } from '../../types/reservationType';
import { capitalize } from '../../utils/textUtils';
import './ReservationCalendarEvent.scss';
import { Capacitor } from '@capacitor/core';
import { useOrganizationContext } from '../../context/OrganizationContext';
import { PrimeIcons } from 'primereact/api';
import classNames from 'classnames';
import { ReservationTypeWithColor } from '../../hooks/AppData/useReservationTypes';
import { Avatar } from 'primereact/avatar';
import { getUserAvatarProps } from '../../utils/avatarUtils';

interface Props extends EventProps<Reservation> {
  reservationType: ReservationTypeWithColor;
  showAttendance?: boolean;
  externalUserId?: string;
  showBranch?: boolean;
}

const getSingleEventTitle = (reservation: Reservation) => {
  const eventExternalUser = reservation.confirmedExternalUsers?.[0] ?? reservation.unregisteredClient;
  if (!eventExternalUser) return null;
  return `${eventExternalUser?.name} ${eventExternalUser?.surnames}`;
};

const getGroupEventTitle = (reservation: Reservation, reservationType: ReservationType) => {
  if (reservationType.hasNoGroupSizeLimit) {
    return `${reservation.confirmedExternalUsers?.length ?? 0} asistente${reservation.confirmedExternalUsers?.length === 1 ? '' : 's'}`;
  }
  return `${reservation.confirmedExternalUsers?.length ?? 0}/${reservationType.groupSize}`;
};

// The pill is filled with the saturated data color `--<hue>-<shade>`, which does not follow the theme, so its text cannot
// follow the theme either. Dark text (--on-pill-text) reaches 4.5:1 on the -500 of every hue except these two, which take
// white: bluegray, and indigo, drawn one step darker (-600) because white on indigo-500 is 4.47:1.
export const PILL_TEXT_TOKENS: Record<string, string> = { indigo: '--on-fill-light', bluegray: '--on-fill-light' };
export const PILL_FILL_SHADE: Record<string, number> = { indigo: 600 };
const pillFill = (hue: string) => `var(--${hue}-${PILL_FILL_SHADE[hue] ?? 500})`;

export const getReservationEventColor = (
  reservation: Reservation,
  reservationType: ReservationTypeWithColor | undefined,
  missedAttendance: boolean = false,
) => {
  if (!reservationType) return 'bluegray';
  const userCanceled = reservation.externalUserStatus === RESERVATION_STATUSES.CANCELED;
  if (missedAttendance || userCanceled) return 'pink';
  if (reservation.blocked) return 'bluegray';
  return reservationType.color;
};

export const checkIfCurrentUserMissedAttendance = (
  reservation: Reservation,
  externalUserId: string | undefined,
  reservationType: ReservationTypeWithColor | undefined,
) => {
  if (!externalUserId || !reservationType) return { hasAttendanceRecords: false, currentUserDidNotAttend: false };
  const isPast = new Date(reservation.localEndDate) <= new Date();
  const hasAttendanceRecords =
    isPast && reservation.externalUsers?.some((user) => user.id === externalUserId && user.attendance !== undefined);
  const currentUser = reservation.externalUsers?.find((user) => user.id === externalUserId);
  const currentUserDidNotAttend = currentUser && currentUser.attendance === false;

  return { hasAttendanceRecords, currentUserDidNotAttend };
};

export const ReservationCalendarEvent: React.FC<Props> = ({
  event: reservation,
  reservationType,
  showAttendance = false,
  externalUserId,
  showBranch = false,
}) => {
  const { organization, orgBranches } = useOrganizationContext();
  const branchColor = orgBranches?.find((b) => b.id === reservation.branch.id)?.color ?? '#000000';
  const tooltipId = `event-${reservation.id}`;
  const isGroup = reservationType.groupSize > 1;
  const isTouchDevice = Capacitor.isNativePlatform() || window.matchMedia('(hover: none)').matches;
  const userCanceled = reservation.externalUserStatus === RESERVATION_STATUSES.CANCELED;
  const canceledByAdmin = reservation.canceledBy === CANCELED_BY_OPTIONS.CANCELED_BY_ADMIN;
  const isInQueue = reservation.externalUserStatus === RESERVATION_STATUSES.IN_QUEUE;
  const canceledString = canceledByAdmin ? `Cancelada por ${capitalize(organization?.name)}` : 'Cancelada';

  const title = isGroup ? null : getSingleEventTitle(reservation);
  const capacity = isGroup ? getGroupEventTitle(reservation, reservationType) : null;

  const time = (
    <>
      <span>{format(parseISO(reservation.localStartDate), 'HH:mm', { locale: es })}</span>{' '}
      <span className='event-time-end'>- {format(parseISO(reservation.localEndDate), 'HH:mm', { locale: es })}</span>
    </>
  );

  const { hasAttendanceRecords, currentUserDidNotAttend } = checkIfCurrentUserMissedAttendance(
    reservation,
    externalUserId,
    reservationType,
  );
  const calendarReservationDidNotAttend =
    !externalUserId &&
    !isGroup &&
    reservationType.hasAttendance &&
    reservation.externalUsers?.length &&
    reservation.externalUsers?.every((user) => !user.attendance);
  const color = getReservationEventColor(reservation, reservationType, currentUserDidNotAttend);

  return (
    <div id={tooltipId} className='calendar-event-container'>
      <div className='calendar-event-inside' style={{ borderColor: branchColor }}>
        <div
          className='event-type'
          style={{
            color: `var(${PILL_TEXT_TOKENS[color || 'bluegray'] ?? '--on-pill-text'})`,
            backgroundColor: pillFill(color || 'bluegray'),
            border: `1px solid ${pillFill(color || 'bluegray')}`,
          }}
        >
          <span className='event-type-branch-dot' style={{ backgroundColor: branchColor }} />
          {capitalize(reservationType.hasCustomName ? reservation.customName : reservationType.name)}
        </div>
        {reservation.title && <div className='event-custom-title'>{reservation.title}</div>}
        {showBranch && (
          <div className='event-branch'>
            <i className='pi pi-map-marker' />
            {reservation.branch.name}
          </div>
        )}
        <div
          className={classNames('event-title', {
            'reservation-calendar-did-not-attend': calendarReservationDidNotAttend,
          })}
        >
          {title}
        </div>
        {reservation.user && !externalUserId && (
          <div className='event-professional'>
            <Avatar shape='circle' {...getUserAvatarProps(reservation.user)} className='event-professional-avatar' />{' '}
            {reservation.user.name}
          </div>
        )}
        {reservation.informativeUser && !externalUserId && (
          <div className='event-informative-professional'>
            <i className='pi pi-user' />
            {reservation.informativeUser.name} {reservation.informativeUser.surnames}
          </div>
        )}
        {capacity && <div className='event-capacity'>{reservation.blocked ? 'Bloqueada' : capacity}</div>}
        {!userCanceled && !currentUserDidNotAttend && !isInQueue && <div className='event-time'>{time}</div>}
        {userCanceled && (
          <div className='event-status'>
            <span
              className={classNames('cancel-by-badge', canceledByAdmin ? 'cancel-by-employee' : 'cancel-by-client')}
              title={canceledByAdmin ? 'Cancelada por empleado' : 'Cancelada por cliente'}
            >
              {canceledByAdmin ? 'E' : 'C'}
            </span>
            {canceledString}
            {reservation.cancellationDate && (
              <span className='event-cancel-time'>
                {' '}
                ({format(parseISO(reservation.cancellationDate), 'HH:mm', { locale: es })})
              </span>
            )}
          </div>
        )}
        {currentUserDidNotAttend && <div className='event-status'>No asistió</div>}
        {isInQueue && (
          <div className='event-status' style={{ color: 'var(--text-muted)' }}>
            En cola
          </div>
        )}
      </div>
      {!isTouchDevice && (
        <Tooltip target={`#${tooltipId}`} position='right' showDelay={50} className='calendar-event-tooltip' hideDelay={0}>
          <div className='event-tooltip'>
            <strong>{capitalize(reservationType.hasCustomName ? reservation.customName : reservationType.name)}</strong>
            {reservation.title && <span className='event-tooltip-title'>{reservation.title}</span>}
            <div className='event-details'>
              {userCanceled && (
                <div>
                  <i className={classNames(PrimeIcons.TIMES_CIRCLE, 'error')} />
                  <span>
                    {canceledByAdmin ? `Cancelada por ${capitalize(organization?.name)}` : 'Cancelada por el cliente'}
                    {reservation.cancellationDate &&
                      ` · ${format(parseISO(reservation.cancellationDate), "d 'de' MMM 'a las' HH:mm", { locale: es })}`}
                  </span>
                </div>
              )}
              {showAttendance && hasAttendanceRecords && (
                <div>
                  <i
                    className={`pi ${currentUserDidNotAttend ? 'pi-user-minus' : 'pi-user-plus'}`}
                    style={{ color: currentUserDidNotAttend ? 'var(--danger)' : 'var(--success)' }}
                  />
                  <span>{currentUserDidNotAttend ? 'No asistió' : 'Asistió'}</span>
                </div>
              )}
              {isInQueue && (
                <div>
                  <i className='pi pi-stopwatch' style={{ color: 'var(--accent)' }} />
                  <span>Esperando en cola</span>
                </div>
              )}
              <div>
                <i className='pi pi-calendar' />
                <span>{capitalize(format(parseISO(reservation.localStartDate), "EEEE, d 'de' MMMM", { locale: es }))}</span>
              </div>
              <div>
                <i className='pi pi-clock' />
                <span>{time}</span>
              </div>
              <div>
                <i className='pi pi-map-marker' />
                <span>{reservation.branch.name}</span>
              </div>
              {isGroup ? (
                <>
                  <div>
                    <i className='pi pi-users' />
                    <span>Sesión de grupo ({reservation.blocked ? 'Bloqueada' : capacity})</span>
                  </div>
                  <div className='event-tooltip-attendees'>
                    {reservation.confirmedExternalUsers?.map((user) => {
                      return (
                        <span
                          className={classNames({
                            'reservation-calendar-did-not-attend': user.attendance === false,
                          })}
                          key={user.id}
                        >
                          {user.name} {user.surnames}
                          {user.bookedFromApp ? ' (app)' : ''}
                        </span>
                      );
                    })}
                  </div>
                </>
              ) : (
                title && (
                  <>
                    <div
                      className={classNames({
                        'reservation-calendar-did-not-attend': calendarReservationDidNotAttend || currentUserDidNotAttend,
                      })}
                    >
                      <i className='pi pi-user' />
                      <span>{title}</span>
                    </div>
                  </>
                )
              )}
              {reservation.user && (
                <div>
                  <Avatar
                    shape='circle'
                    size='normal'
                    {...getUserAvatarProps(reservation.user)}
                    className='event-professional-avatar'
                  />
                  <span>
                    Profesional: {reservation.user.name} {reservation.user.surnames}
                  </span>
                </div>
              )}
              {reservation.informativeUser && (
                <div>
                  <i className='pi pi-user' />
                  <span>
                    Empleado: {reservation.informativeUser.name} {reservation.informativeUser.surnames}
                  </span>
                </div>
              )}
            </div>
          </div>
        </Tooltip>
      )}
    </div>
  );
};
